// Concurrent Binary Tree (CBT) for adaptive terrain tessellation.
// Port of cbt.rs.
//
// Two root triangles (keys 2,3 at depth 1) subdivide the unit square diagonally.
// Convention: triangle (a, b, c) where a=apex, b,c=base (hypotenuse).

import { f16ToF32 } from './math';

const CBT_MAX_DEPTH = 18;
const MAX_TRIANGLES = 1 << CBT_MAX_DEPTH;

// Reusable typed array for floatBits — avoids allocation per call
const _fbBuf = new Float32Array(1);
const _fbView = new Uint32Array(_fbBuf.buffer);

function floatBits(f: number): number {
  _fbBuf[0] = f;
  return _fbView[0];
}

// --- Triangle key arithmetic ---

function triDepth(key: number): number {
  return 31 - Math.clz32(key);
}

function triParent(key: number): number {
  return key >>> 1;
}

function triLeftChild(key: number): number {
  return key << 1;
}

function triRightChild(key: number): number {
  return (key << 1) | 1;
}

function triSibling(key: number): number {
  return key ^ 1;
}

// triVertices using flat output to avoid tuple allocation
// Writes to caller-provided arrays, returns nothing
function triVerticesFlat(
  key: number,
  a: Float64Array, // [u, v] at offset 0
  b: Float64Array, // [u, v] at offset 0
  c: Float64Array, // [u, v] at offset 0
): void {
  const depth = triDepth(key);

  if (((key >>> (depth - 1)) & 1) === 0) {
    a[0] = 0; a[1] = 0; b[0] = 1; b[1] = 0; c[0] = 0; c[1] = 1;
  } else {
    a[0] = 1; a[1] = 1; b[0] = 0; b[1] = 1; c[0] = 1; c[1] = 0;
  }

  for (let i = depth - 2; i >= 0; i--) {
    const bit = (key >>> i) & 1;
    const midU = (b[0] + c[0]) * 0.5;
    const midV = (b[1] + c[1]) * 0.5;
    if (bit === 0) {
      c[0] = b[0]; c[1] = b[1];
      b[0] = a[0]; b[1] = a[1];
      a[0] = midU; a[1] = midV;
    } else {
      b[0] = c[0]; b[1] = c[1];
      c[0] = a[0]; c[1] = a[1];
      a[0] = midU; a[1] = midV;
    }
  }
}

function triDiamondNeighbor(key: number): number {
  const depth = triDepth(key);
  if (depth <= 1) return triSibling(key);

  const rootBit = (key >>> (depth - 1)) & 1;
  let neighborKey = (1 << 1) | (rootBit ^ 1);

  for (let i = 0; i < depth - 1; i++) {
    const bit = (key >>> i) & 1;
    neighborKey = (neighborKey << 1) | (bit ^ 1);
  }

  return neighborKey;
}

// --- Vertex dedup using two-level numeric Map ---

class VertexDedup {
  private outer = new Map<number, Map<number, number>>();

  get(bitsU: number, bitsV: number): number | undefined {
    return this.outer.get(bitsU)?.get(bitsV);
  }

  set(bitsU: number, bitsV: number, idx: number): void {
    let inner = this.outer.get(bitsU);
    if (!inner) {
      inner = new Map<number, number>();
      this.outer.set(bitsU, inner);
    }
    inner.set(bitsV, idx);
  }
}

// --- Edge dedup using vertex index pairs ---
// Pack as min(a,b) * 524288 + max(a,b). Safe for vertex count < 524288.

function edgeKey(ia: number, ib: number): number {
  const lo = ia < ib ? ia : ib;
  const hi = ia < ib ? ib : ia;
  return lo * 524288 + hi;
}

// --- Cbt class ---

export class Cbt {
  leaves: Set<number>;
  lodThreshold = 4.0;
  private heightmapU16: Uint16Array;
  private heightmapRes: number;
  private elevRange: number;

  // Mesh cache
  private _meshDirty = true;
  private _cachedVertices: Float32Array | null = null;
  private _cachedIndices: Uint32Array | null = null;
  private _cachedElevScale = 0;

  // Reusable scratch arrays for triVerticesFlat
  private _a = new Float64Array(2);
  private _b = new Float64Array(2);
  private _c = new Float64Array(2);

  constructor(heightmapData: ArrayBuffer, res: number, elevMin: number, elevMax: number) {
    this.leaves = new Set([2, 3]);
    this.heightmapU16 = new Uint16Array(heightmapData);
    this.heightmapRes = res;
    this.elevRange = Math.abs(elevMax - elevMin);
  }

  private sampleRaw(x: number, y: number): number {
    const idx = y * this.heightmapRes + x;
    return idx < this.heightmapU16.length ? f16ToF32(this.heightmapU16[idx]) : 0;
  }

  private sampleElev(u: number, v: number): number {
    const res = this.heightmapRes;
    const x = Math.max(0, Math.min(res - 1, u * (res - 1)));
    const y = Math.max(0, Math.min(res - 1, v * (res - 1)));
    const x0 = x | 0;
    const y0 = y | 0;
    const x1 = Math.min(x0 + 1, res - 1);
    const y1 = Math.min(y0 + 1, res - 1);
    const fx = x - x0;
    const fy = y - y0;

    const s00 = this.sampleRaw(x0, y0);
    const s10 = this.sampleRaw(x1, y0);
    const s01 = this.sampleRaw(x0, y1);
    const s11 = this.sampleRaw(x1, y1);

    const top = s00 * (1 - fx) + s10 * fx;
    const bot = s01 * (1 - fx) + s11 * fx;
    return top * (1 - fy) + bot * fy;
  }

  private screenError(
    key: number,
    camPosX: number, camPosY: number, camPosZ: number,
    screenH: number, elevScale: number,
  ): number {
    triVerticesFlat(key, this._a, this._b, this._c);
    const b = this._b, c = this._c;
    const midU = (b[0] + c[0]) * 0.5;
    const midV = (b[1] + c[1]) * 0.5;

    const actualElev = this.sampleElev(midU, midV);
    const interpElev = (this.sampleElev(b[0], b[1]) + this.sampleElev(c[0], c[1])) * 0.5;
    const heightError = Math.abs(actualElev - interpElev) * 0.00001;

    const edgeDu = b[0] - c[0], edgeDv = b[1] - c[1];
    const edgeLen = Math.sqrt(edgeDu * edgeDu + edgeDv * edgeDv);
    const totalError = heightError + edgeLen * 0.1;

    const worldY = actualElev * 0.00001 * elevScale;
    const dx = (midU - 0.5) - camPosX;
    const dy = worldY - camPosY;
    const dz = (midV - 0.5) - camPosZ;
    const dist = Math.max(0.001, Math.sqrt(dx * dx + dy * dy + dz * dz));

    return (totalError / dist) * (screenH * 0.5);
  }

  update(
    camPosX: number, camPosY: number, camPosZ: number,
    screenW: number, screenH: number, elevScale: number,
  ): void {
    const maxLeaves = MAX_TRIANGLES;
    const splitThreshold = this.lodThreshold;
    const mergeThreshold = this.lodThreshold * 0.5;
    const sizeBefore = this.leaves.size;

    // Split pass
    const toSplit: number[] = [];
    for (const key of this.leaves) {
      const depth = triDepth(key);
      const err = this.screenError(key, camPosX, camPosY, camPosZ, screenH, elevScale);
      if (err > splitThreshold && depth < CBT_MAX_DEPTH) {
        toSplit.push(key);
      }
    }

    for (const key of toSplit) {
      if (!this.leaves.has(key) || this.leaves.size >= maxLeaves - 4) continue;
      this.conformantSplit(key, maxLeaves);
    }

    // Merge pass
    let mergedAny = false;
    const merged = new Set<number>();
    for (const key of this.leaves) {
      const depth = triDepth(key);
      if (depth <= 1 || merged.has(key)) continue;
      const sib = triSibling(key);
      if (!this.leaves.has(sib) || merged.has(sib)) continue;

      const parentKey = triParent(key);
      const err = this.screenError(parentKey, camPosX, camPosY, camPosZ, screenH, elevScale);

      if (err < mergeThreshold) {
        const diamond = triDiamondNeighbor(parentKey);
        const diamondIsLeaf = this.leaves.has(diamond);
        const diamondParentIsLeaf = triDepth(diamond) > 1 && this.leaves.has(triParent(diamond));
        if (diamondIsLeaf || diamondParentIsLeaf) {
          merged.add(key);
          merged.add(sib);
          mergedAny = true;
        }
      }
    }

    if (mergedAny) {
      const finalLeaves = new Set<number>();
      const parentsAdded = new Set<number>();
      for (const key of this.leaves) {
        if (merged.has(key)) {
          const parent = triParent(key);
          if (!parentsAdded.has(parent)) {
            parentsAdded.add(parent);
            finalLeaves.add(parent);
          }
        } else {
          finalLeaves.add(key);
        }
      }
      this.leaves = finalLeaves;
    }

    // Mark mesh dirty only if leaves actually changed
    if (this.leaves.size !== sizeBefore || toSplit.length > 0 || mergedAny) {
      this._meshDirty = true;
    }
  }

  private conformantSplit(key: number, maxLeaves: number): void {
    if (triDepth(key) >= CBT_MAX_DEPTH || this.leaves.size >= maxLeaves - 4) return;
    if (!this.leaves.has(key)) return;

    const diamond = triDiamondNeighbor(key);
    const dDepth = triDepth(diamond);
    const kDepth = triDepth(key);

    if (dDepth === kDepth && this.leaves.has(diamond)) {
      this.leaves.delete(key);
      this.leaves.add(triLeftChild(key));
      this.leaves.add(triRightChild(key));
      this.leaves.delete(diamond);
      this.leaves.add(triLeftChild(diamond));
      this.leaves.add(triRightChild(diamond));
    } else if (dDepth < kDepth) {
      this.conformantSplit(diamond, maxLeaves);
      if (this.leaves.has(key)) {
        this.conformantSplit(key, maxLeaves);
      }
    } else {
      this.leaves.delete(key);
      this.leaves.add(triLeftChild(key));
      this.leaves.add(triRightChild(key));
    }
  }

  /** Generate mesh vertices (pos xyz + uv) and indices, with skirt geometry.
   *  Returns cached result if CBT hasn't changed since last call. */
  generateMesh(elevScale: number): { vertices: Float32Array; indices: Uint32Array } {
    if (!this._meshDirty && this._cachedVertices && this._cachedElevScale === elevScale) {
      return { vertices: this._cachedVertices, indices: this._cachedIndices! };
    }

    const verts: number[] = [];  // [x, y, z, u, v, ...]
    const idxs: number[] = [];
    const vertexMap = new VertexDedup();
    const edgeSet = new Set<number>();
    // Store edge endpoint vertex indices for skirt generation
    const edgeList: number[] = []; // pairs: [ia, ib, ia, ib, ...]

    const a = new Float64Array(2), b = new Float64Array(2), c = new Float64Array(2);

    const getOrInsert = (u: number, v: number, yOffset: number): number => {
      const bu = floatBits(u), bv = floatBits(v);
      const existing = vertexMap.get(bu, bv);
      if (existing !== undefined) return existing;
      const idx = (verts.length / 5) | 0;
      verts.push(u - 0.5, yOffset, v - 0.5, u, v);
      vertexMap.set(bu, bv, idx);
      return idx;
    };

    for (const key of this.leaves) {
      triVerticesFlat(key, a, b, c);
      const ia = getOrInsert(a[0], a[1], 0);
      const ib = getOrInsert(b[0], b[1], 0);
      const ic = getOrInsert(c[0], c[1], 0);
      idxs.push(ia, ib, ic);

      // Deduplicate edges by vertex index pair
      const eAB = edgeKey(ia, ib);
      const eBC = edgeKey(ib, ic);
      const eCA = edgeKey(ic, ia);
      if (!edgeSet.has(eAB)) { edgeSet.add(eAB); edgeList.push(ia, ib); }
      if (!edgeSet.has(eBC)) { edgeSet.add(eBC); edgeList.push(ib, ic); }
      if (!edgeSet.has(eCA)) { edgeSet.add(eCA); edgeList.push(ic, ia); }
    }

    // Skirt quads — look up UV from vertex array by index
    const skirtDrop = -(this.elevRange * 0.00001 * elevScale + 0.001);
    for (let e = 0; e < edgeList.length; e += 2) {
      const ipTop = edgeList[e];
      const iqTop = edgeList[e + 1];

      // Read UVs from vertex data (offset +3, +4 in stride of 5)
      const pu = verts[ipTop * 5 + 3], pv = verts[ipTop * 5 + 4];
      const qu = verts[iqTop * 5 + 3], qv = verts[iqTop * 5 + 4];

      // Skirt vertices — always fresh (not deduplicated with surface)
      const ipBot = (verts.length / 5) | 0;
      verts.push(pu - 0.5, skirtDrop, pv - 0.5, pu, pv);
      const iqBot = (verts.length / 5) | 0;
      verts.push(qu - 0.5, skirtDrop, qv - 0.5, qu, qv);

      idxs.push(ipTop, iqTop, iqBot);
      idxs.push(ipTop, iqBot, ipBot);
    }

    const vertices = new Float32Array(verts);
    const indices = new Uint32Array(idxs);

    this._cachedVertices = vertices;
    this._cachedIndices = indices;
    this._cachedElevScale = elevScale;
    this._meshDirty = false;

    return { vertices, indices };
  }

  leafCount(): number {
    return this.leaves.size;
  }
}
